import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  HomeIcon,
  HomeModernIcon,
  MoonIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  SparklesIcon,
  SunIcon,
  UserIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/16/solid';
import {
  hoaService,
  homeService,
  memberService,
  violationService,
} from '../clients/ApiClients';
import {
  HoaReturn,
  HomeReturn,
  MemberReturn,
  ReservationReturn,
  ReservationSeriesReturn,
  VendorReturn,
  ViolationReturn,
  WorkOrderReturn,
} from '../clients/rust-api-client';
import { useUser } from '../hooks/UserContext';
import { useTheme } from '../common/Custom/ThemeProvider';
import {
  UserType,
  canCreateHoa,
  canCreateVendor,
  canCreateViolation,
  canCreateWorkOrder,
  canManageReservations,
  canManageUsers,
  canSendMemberMessage,
  canViewBankAccounts,
  canViewReservations,
} from '../constants/UserTypes';
import { StatusIcon } from '../violation/linear/StatusIcon';
import { shortRef, statusLabel } from '../violation/linear/violationMeta';
import { homeAddressLine, homeLocality } from '../home/homeMeta';
import { CommandPalette, usePaletteShortcut } from './CommandPalette';
import { Command, CommandGroup } from './commandRanking';
import { CreateScope, scopeFromPath } from './createScope';
import { CreateViolationModal } from '../violation/linear/CreateViolationModal';
import { CreateWorkOrderModal } from '../workorder/linear/CreateWorkOrderModal';
import { SendMessageModal } from '../communication/SendMessageModal';
import {
  CreateReservationModal,
  ReservationPrefill,
} from '../reservation/CreateReservationModal';
import { CreateSeriesModal } from '../reservation/CreateSeriesModal';
import { CreateHoaModal } from '../hoa/linear/CreateHoaModal';
import { CreateVendorModal } from '../vendor/linear/CreateVendorModal';
import { useHoaDirectory } from '../hoa/useHoaDirectory';
import {
  useSyncedHoas,
  useSyncedHomes,
  useSyncedMemberPlacements,
  useSyncedViolations,
} from '../sync/hooks';

/**
 * Mounts the ⌘K palette once for the whole signed-in app (#603 follow-on
 * 4). Not in the sidebar: the layout renders the sidebar twice on a phone
 * — the hidden desktop copy and the drawer — and two palettes would both
 * answer the shortcut.
 *
 * It also owns create (#632): new violation, new work order, send
 * message, and (#665) new association and new vendor are one modal each,
 * mounted here, whether they were opened from
 * a list's button, the sidebar's "New…" menu or the palette. Opened from
 * the sidebar or the palette, a modal starts from the page you are on
 * (`scopeFromPath`) and then opens what it made; a list passes its own
 * scope, locked, and takes the result itself.
 *
 * There is no search endpoint, so the palette searches what it can list:
 * associations and the person's violations, then every association's homes
 * and members, fetched when it first opens and kept for a couple of
 * minutes. Fine at the size of a management company's book; the thing to
 * replace with a server-side search if that stops being true.
 */
export interface OpenCreateOptions<T> {
  /** Where it is for. Defaults to what the current URL implies. */
  scope?: CreateScope;
  /** The caller's scope is fixed: those chips are shown, not pickable. */
  lockScope?: boolean;
  /** Takes the result. Without it, the new item is opened. */
  onCreated?: (created: T) => void;
}

/** A reservation can also start from a calendar cell: amenity, day, time. */
export type OpenReservationOptions = OpenCreateOptions<ReservationReturn> & {
  prefill?: ReservationPrefill;
};

interface PaletteContextType {
  openPalette: () => void;
  openNewViolation: (options?: OpenCreateOptions<ViolationReturn>) => void;
  openNewWorkOrder: (options?: OpenCreateOptions<WorkOrderReturn>) => void;
  openSendMessage: (options?: OpenCreateOptions<void>) => void;
  openNewReservation: (options?: OpenReservationOptions) => void;
  /** A recurring booking for one household (P2). */
  openNewReservationSeries: (options?: OpenCreateOptions<ReservationSeriesReturn>) => void;
  /** A new association has no scope: it is the scope. */
  openNewHoa: (options?: Pick<OpenCreateOptions<HoaReturn>, 'onCreated'>) => void;
  openNewVendor: (options?: OpenCreateOptions<VendorReturn>) => void;
  /** Subscribe to sends from anywhere; returns the unsubscribe. */
  onMessageSent: (listener: () => void) => () => void;
}

const PaletteContext = createContext<PaletteContextType>({
  openPalette: () => undefined,
  openNewViolation: () => undefined,
  openNewWorkOrder: () => undefined,
  openSendMessage: () => undefined,
  openNewReservation: () => undefined,
  openNewReservationSeries: () => undefined,
  openNewHoa: () => undefined,
  openNewVendor: () => undefined,
  onMessageSent: () => () => undefined,
});

export const useCommandPalette = () => useContext(PaletteContext);

/**
 * Refresh a view of sent messages whenever one is sent — including from
 * the palette or the sidebar while the view is open.
 */
export const useMessageSentListener = (listener: () => void) => {
  const { onMessageSent } = useCommandPalette();
  const latest = useRef(listener);
  latest.current = listener;
  useEffect(() => onMessageSent(() => latest.current()), [onMessageSent]);
};

type OpenCreate =
  | ({ kind: 'violation' } & OpenCreateOptions<ViolationReturn>)
  | ({ kind: 'workOrder' } & OpenCreateOptions<WorkOrderReturn>)
  | ({ kind: 'message' } & OpenCreateOptions<void>)
  | ({ kind: 'reservation' } & OpenReservationOptions)
  | ({ kind: 'reservationSeries' } & OpenCreateOptions<ReservationSeriesReturn>)
  | ({ kind: 'hoa' } & OpenCreateOptions<HoaReturn>)
  | ({ kind: 'vendor' } & OpenCreateOptions<VendorReturn>);

const FRESH_FOR_MS = 2 * 60 * 1000;
const PUBLIC_PATHS = ['/login', '/callback'];

interface Searchable {
  hoas: HoaReturn[];
  violations: ViolationReturn[];
  homes: HomeReturn[];
  members: { member: MemberReturn; hoaId: string }[];
}

const EMPTY: Searchable = { hoas: [], violations: [], homes: [], members: [] };

export const CommandPaletteProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { userId, userType, companyId } = useUser();
  const signedIn = !!userId && !PUBLIC_PATHS.includes(pathname);

  const [open, setOpen] = useState(false);
  const [create, setCreate] = useState<OpenCreate | null>(null);
  const messageListeners = useRef(new Set<() => void>());
  const [data, setData] = useState<Searchable>(EMPTY);

  // With the sync engine's local copy (#561) there is nothing to load: the
  // palette searches what is already in memory, and it is always current.
  // The per-association fetching below is the fallback.
  const syncedHoas = useSyncedHoas();
  const syncedHomes = useSyncedHomes({});
  const syncedMembers = useSyncedMemberPlacements();
  const syncedViolations = useSyncedViolations({});
  const synced = useMemo((): Searchable | null => {
    if (!syncedHoas || !syncedHomes || !syncedMembers || !syncedViolations) return null;
    return {
      hoas: syncedHoas.filter((h) => h.is_active !== false),
      homes: syncedHomes,
      members: syncedMembers,
      violations: syncedViolations,
    };
  }, [syncedHoas, syncedHomes, syncedMembers, syncedViolations]);
  const haveSynced = useRef(false);
  haveSynced.current = synced !== null;
  const searchable = synced ?? data;
  const [loading, setLoading] = useState(false);
  const loadedAt = useRef(0);
  const inFlight = useRef(false);
  const currentUser = useRef(userId);

  // A different person, or nobody: forget everything the last one could see.
  useEffect(() => {
    currentUser.current = userId;
    setData(EMPTY);
    loadedAt.current = 0;
    setOpen(false);
    setCreate(null);
  }, [userId]);

  const load = useCallback(async () => {
    if (haveSynced.current) return;
    if (inFlight.current || Date.now() - loadedAt.current < FRESH_FOR_MS)
      return;
    inFlight.current = true;
    setLoading(true);
    const forUser = userId;
    // A load that outlives a sign-out must not land in the next session.
    const stale = () => currentUser.current !== forUser;
    try {
      // Same source as the HOAs page and the sidebar.
      const hoaRequest =
        userType === UserType.SUPER_ADMIN
          ? hoaService.getAllHoas()
          : companyId
            ? hoaService.getHoasByCompany({ companyId })
            : null;
      const [hoaRes, vioRes] = await Promise.allSettled([
        hoaRequest ?? Promise.resolve(null),
        violationService.getAllViolationsForUser(),
      ]);
      const hoas =
        hoaRes.status === 'fulfilled' && hoaRes.value
          ? hoaRes.value.data
              .filter((h) => h.is_active !== false)
              .sort((a, b) => a.name.localeCompare(b.name))
          : [];
      const violations =
        vioRes.status === 'fulfilled' ? vioRes.value.data.violations : [];
      if (stale()) return;
      setData((d) => ({ ...d, hoas, violations }));

      // Per association, in parallel. One that fails just contributes
      // nothing; the palette is a shortcut, not the source of truth.
      const perHoa = await Promise.all(
        hoas.map(async (h) => {
          const [homes, members] = await Promise.allSettled([
            homeService.getHomesByHoa({ hoaId: h.id }),
            memberService.getMembersByHoa({ hoaId: h.id }),
          ]);
          return {
            homes: homes.status === 'fulfilled' ? homes.value.data : [],
            members:
              members.status === 'fulfilled'
                ? members.value.data.map((member) => ({ member, hoaId: h.id }))
                : [],
          };
        })
      );
      if (stale()) return;
      setData({
        hoas,
        violations,
        homes: perHoa.flatMap((r) => r.homes),
        members: perHoa.flatMap((r) => r.members),
      });
      loadedAt.current = Date.now();
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [userId, userType, companyId]);

  const setPaletteOpen = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) load();
    },
    [load]
  );
  usePaletteShortcut(signedIn, open, setPaletteOpen);

  const canCreate = canCreateViolation(userType);
  const canCreateWo = canCreateWorkOrder(userType);
  const canMessage = canSendMemberMessage(userType);
  const canBook = canManageReservations(userType);
  const canSeeReservations = canViewReservations(userType);
  const canNewHoa = canCreateHoa(userType);
  const canNewVendor = canCreateVendor(userType);

  // The create modals' association chip: the sync engine's copy when it
  // has one (#561); otherwise the per-person directory the lists' HOA
  // filter shares, loaded the first time a modal opens.
  const directory = useHoaDirectory({
    enabled: signedIn && !!create && !syncedHoas,
    withHomes: false,
  });
  const activeHoas = useMemo(
    () => (syncedHoas ?? directory.hoas).filter((h) => h.is_active !== false),
    [syncedHoas, directory.hoas]
  );

  const openNewViolation = useCallback(
    (options: OpenCreateOptions<ViolationReturn> = {}) =>
      setCreate({ kind: 'violation', ...options }),
    []
  );
  const openNewWorkOrder = useCallback(
    (options: OpenCreateOptions<WorkOrderReturn> = {}) =>
      setCreate({ kind: 'workOrder', ...options }),
    []
  );
  const openSendMessage = useCallback(
    (options: OpenCreateOptions<void> = {}) =>
      setCreate({ kind: 'message', ...options }),
    []
  );
  const openNewReservation = useCallback(
    (options: OpenReservationOptions = {}) =>
      setCreate({ kind: 'reservation', ...options }),
    []
  );
  const openNewReservationSeries = useCallback(
    (options: OpenCreateOptions<ReservationSeriesReturn> = {}) =>
      setCreate({ kind: 'reservationSeries', ...options }),
    []
  );
  const openNewHoa = useCallback(
    (options: Pick<OpenCreateOptions<HoaReturn>, 'onCreated'> = {}) =>
      setCreate({ kind: 'hoa', ...options }),
    []
  );
  const openNewVendor = useCallback(
    (options: OpenCreateOptions<VendorReturn> = {}) =>
      setCreate({ kind: 'vendor', ...options }),
    []
  );
  const onMessageSent = useCallback((listener: () => void) => {
    messageListeners.current.add(listener);
    return () => {
      messageListeners.current.delete(listener);
    };
  }, []);

  const groups = useMemo((): CommandGroup[] => {
    const go = (to: string) => () => navigate(to);
    const hoaName = new Map(searchable.hoas.map((h) => [h.id, h.name]));
    const page = (
      to: string,
      label: string,
      keywords: string[] = []
    ): Command => ({
      id: `page:${to}`,
      label,
      keywords,
      hint: 'Page',
      icon: <ArrowRightIcon className="h-3.5 w-3.5" />,
      run: go(to),
    });

    // On an HOA's pages, say which one a create will start in.
    const here = scopeFromPath(pathname).hoaId;
    const inHoa = here ? hoaName.get(here) : undefined;
    const createCommand = (
      id: string,
      label: string,
      keywords: string[],
      icon: React.ReactNode,
      run: () => void
    ): Command => ({
      id,
      label,
      detail: inHoa ? `in ${inHoa}` : undefined,
      keywords: ['create', 'new', 'add', ...keywords],
      hint: 'Action',
      icon,
      run,
    });

    // Opens a modal; nothing here creates or sends on its own.
    const actions: Command[] = [
      ...(canCreate
        ? [
            createCommand(
              'action:new-violation',
              'New violation…',
              ['report', 'cite'],
              <PencilSquareIcon className="h-3.5 w-3.5" />,
              () => openNewViolation()
            ),
          ]
        : []),
      ...(canCreateWo
        ? [
            createCommand(
              'action:new-work-order',
              'New work order…',
              ['maintenance', 'repair', 'job', 'vendor'],
              <WrenchScrewdriverIcon className="h-3.5 w-3.5" />,
              () => openNewWorkOrder()
            ),
          ]
        : []),
      ...(canMessage
        ? [
            createCommand(
              'action:send-message',
              'Send message…',
              ['message', 'email', 'notify', 'announce', 'communication', 'homeowners'],
              <PaperAirplaneIcon className="h-3.5 w-3.5" />,
              () => openSendMessage()
            ),
          ]
        : []),
      ...(canBook
        ? [
            createCommand(
              'action:new-reservation',
              'New reservation…',
              ['book', 'booking', 'amenity', 'clubhouse', 'court', 'reserve'],
              <CalendarDaysIcon className="h-3.5 w-3.5" />,
              () => openNewReservation()
            ),
            createCommand(
              'action:new-reservation-series',
              'New recurring reservation…',
              ['series', 'repeat', 'weekly', 'league', 'standing', 'recurring', 'book'],
              <CalendarDaysIcon className="h-3.5 w-3.5" />,
              () => openNewReservationSeries()
            ),
          ]
        : []),
      ...(canNewVendor
        ? [
            createCommand(
              'action:new-vendor',
              'New vendor…',
              ['contractor', 'supplier', 'landscaper', 'plumber', 'service'],
              <BuildingStorefrontIcon className="h-3.5 w-3.5" />,
              () => openNewVendor()
            ),
          ]
        : []),
      // An association is not created inside one, so no "in …" detail.
      ...(canNewHoa
        ? [
            {
              ...createCommand(
                'action:new-hoa',
                'New association…',
                ['hoa', 'community', 'onboard', 'neighborhood'],
                <HomeModernIcon className="h-3.5 w-3.5" />,
                () => openNewHoa()
              ),
              detail: undefined,
            },
          ]
        : []),
      {
        id: 'action:theme',
        label: isDarkMode ? 'Switch to light theme' : 'Switch to dark theme',
        keywords: ['theme', 'dark', 'light', 'mode', 'appearance'],
        hint: 'Action',
        icon: isDarkMode ? (
          <SunIcon className="h-3.5 w-3.5" />
        ) : (
          <MoonIcon className="h-3.5 w-3.5" />
        ),
        run: toggleDarkMode,
      },
    ];

    const pages: Command[] = [
      page('/home', 'Home', ['dashboard']),
      page('/violations', 'Violations'),
      page('/work-orders', 'Work orders', ['maintenance', 'repairs']),
      ...(canSeeReservations
        ? [
            page('/reservations', 'Reservations', ['bookings', 'amenities', 'calendar']),
            {
              ...page('/reservations', 'Pending requests', ['approve', 'queue', 'reservations', 'bookings']),
              id: 'page:/reservations#pending',
            },
            ...(here
              ? [
                  page(`/hoa/${here}/amenities`, inHoa ? `Amenities — ${inHoa}` : 'Amenities', [
                    'clubhouse',
                    'pool',
                    'court',
                    'facilities',
                  ]),
                  page(
                    `/hoa/${here}/reservations/utilization`,
                    inHoa ? `Amenity utilization — ${inHoa}` : 'Amenity utilization',
                    ['usage', 'report', 'reservations', 'busy', 'no-shows']
                  ),
                  page(
                    `/hoa/${here}/reservations/revenue`,
                    inHoa ? `Amenity revenue — ${inHoa}` : 'Amenity revenue',
                    ['report', 'fees', 'deposits', 'income', 'reservations', 'money']
                  ),
                  page(
                    `/hoa/${here}/reservations/suspensions`,
                    inHoa ? `Booking suspensions — ${inHoa}` : 'Booking suspensions',
                    ['suspend', 'no-shows', 'banned', 'reservations', 'barred']
                  ),
                  page(`/hoa/${here}/amenities#groups`, inHoa ? `Amenity groups — ${inHoa}` : 'Amenity groups', [
                    'courts',
                    'rooms',
                    'any court',
                    'shared limits',
                  ]),
                ]
              : []),
          ]
        : []),
      page('/notifications', 'Notifications', ['inbox', 'messages']),
      page('/communications', 'Communications', ['sent', 'messages', 'email', 'homeowners']),
      page('/hoas', 'HOAs', ['associations']),
      page('/vendors', 'Vendors', ['contractors']),
      ...(canViewBankAccounts(userType)
        ? [page('/banking', 'Banking', ['bank', 'accounts'])]
        : []),
      ...(canManageUsers(userType)
        ? [page('/users', 'Users', ['team', 'staff'])]
        : []),
      page('/settings', 'Settings', ['profile', 'preferences']),
    ];

    return [
      { key: 'actions', label: 'Actions', items: actions },
      { key: 'pages', label: 'Go to', items: pages },
      {
        key: 'hoas',
        label: 'HOAs',
        idleLimit: 5,
        items: searchable.hoas.map((h) => ({
          id: `hoa:${h.id}`,
          label: h.name,
          keywords: ['hoa', 'association'],
          hint: 'HOA',
          icon: <HomeModernIcon className="h-3.5 w-3.5" />,
          run: go(`/hoa/${h.id}`),
        })),
      },
      // Each association's amenities (#642): found by typing "amenities",
      // never listed before you do.
      ...(canSeeReservations
        ? [
            {
              key: 'amenities',
              label: 'Amenities',
              idleLimit: 0,
              items: searchable.hoas.map((h) => ({
                id: `amenities:${h.id}`,
                label: `Amenities — ${h.name}`,
                keywords: ['amenity', 'amenities', 'clubhouse', 'pool', 'court', 'reservations'],
                hint: 'Page',
                icon: <SparklesIcon className="h-3.5 w-3.5" />,
                run: go(`/hoa/${h.id}/amenities`),
              })),
            },
          ]
        : []),
      {
        key: 'homes',
        label: 'Homes',
        idleLimit: 0,
        items: searchable.homes.map((h) => ({
          id: `home:${h.id}`,
          label: homeAddressLine(h) || h.id,
          detail: [hoaName.get(h.hoa_id), homeLocality(h)]
            .filter(Boolean)
            .join(' · '),
          hint: 'Home',
          icon: <HomeIcon className="h-3.5 w-3.5" />,
          run: go(`/hoa/${h.hoa_id}/home/${h.id}`),
        })),
      },
      {
        key: 'members',
        label: 'Members',
        idleLimit: 0,
        items: searchable.members.map(({ member: m, hoaId }) => ({
          id: `member:${hoaId}:${m.id}`,
          label: m.full_name || m.display_name || m.email,
          detail: [m.email, hoaName.get(hoaId)].filter(Boolean).join(' · '),
          keywords: [m.display_name, m.phone ?? ''].filter(Boolean),
          hint: 'Member',
          icon: <UserIcon className="h-3.5 w-3.5" />,
          run: go(`/hoa/${hoaId}/member/${m.id}`),
        })),
      },
      {
        key: 'violations',
        label: 'Violations',
        idleLimit: 0,
        items: searchable.violations.map((v) => ({
          id: `violation:${v.id}`,
          label: v.description,
          detail: `${shortRef(v.id)} · ${statusLabel(v.status)}`,
          keywords: [v.violation_type, v.severity, v.id],
          hint: 'Violation',
          icon: <StatusIcon status={v.status} />,
          run: go(`/violations/${v.id}`),
        })),
      },
    ];
  }, [
    searchable,
    navigate,
    pathname,
    canCreate,
    canCreateWo,
    canMessage,
    canBook,
    canSeeReservations,
    canNewHoa,
    canNewVendor,
    openNewViolation,
    openNewWorkOrder,
    openSendMessage,
    openNewReservation,
    openNewReservationSeries,
    openNewHoa,
    openNewVendor,
    isDarkMode,
    toggleDarkMode,
    userType,
  ]);

  const context = useMemo(
    () => ({
      openPalette: () => setPaletteOpen(true),
      openNewViolation,
      openNewWorkOrder,
      openSendMessage,
      openNewReservation,
      openNewReservationSeries,
      openNewHoa,
      openNewVendor,
      onMessageSent,
    }),
    [
      setPaletteOpen,
      openNewViolation,
      openNewWorkOrder,
      openSendMessage,
      openNewReservation,
      openNewReservationSeries,
      openNewHoa,
      openNewVendor,
      onMessageSent,
    ]
  );

  // A modal opened without a scope starts from the page it was opened on.
  const scope = create?.scope ?? scopeFromPath(pathname);
  const closeCreate = () => setCreate(null);
  const modalCommon = {
    hoas: activeHoas,
    hoasLoading: !syncedHoas && !directory.ready && activeHoas.length === 0,
    scope,
    lockScope: !!create?.scope && !!create.lockScope,
    onClose: closeCreate,
  };

  return (
    <PaletteContext.Provider value={context}>
      {children}
      {signedIn && (
        <>
          <CommandPalette
            open={open}
            onClose={() => setOpen(false)}
            groups={groups}
            loading={loading && !synced}
            placeholder="Search homes, members, violations — or run a command…"
          />
          {canCreate && (
            <CreateViolationModal
              {...modalCommon}
              open={create?.kind === 'violation'}
              onCreated={(v) =>
                create?.kind === 'violation' && create.onCreated
                  ? create.onCreated(v)
                  : navigate(`/violations/${v.id}`)
              }
            />
          )}
          {canCreateWo && (
            <CreateWorkOrderModal
              {...modalCommon}
              open={create?.kind === 'workOrder'}
              onCreated={(w) =>
                create?.kind === 'workOrder' && create.onCreated
                  ? create.onCreated(w)
                  : navigate(`/work-orders/${w.id}`)
              }
            />
          )}
          {canMessage && (
            <SendMessageModal
              {...modalCommon}
              open={create?.kind === 'message'}
              onSent={() => {
                messageListeners.current.forEach((listener) => listener());
                if (create?.kind !== 'message') return;
                if (create.onCreated) create.onCreated();
                // One send to an HOA is a row per member, so there is no
                // single item to open: open the log it landed in.
                else if (pathname !== '/communications') navigate('/communications');
              }}
            />
          )}
          {canBook && (
            <CreateReservationModal
              {...modalCommon}
              open={create?.kind === 'reservation'}
              prefill={create?.kind === 'reservation' ? create.prefill : undefined}
              onCreated={(r) =>
                create?.kind === 'reservation' && create.onCreated
                  ? create.onCreated(r)
                  : navigate(`/hoa/${r.hoa_id}/reservations/${r.id}`)
              }
            />
          )}
          {canBook && (
            <CreateSeriesModal
              {...modalCommon}
              open={create?.kind === 'reservationSeries'}
              onCreated={(series) =>
                create?.kind === 'reservationSeries' && create.onCreated
                  ? create.onCreated(series)
                  : navigate(`/hoa/${series.hoa_id}/reservations/series/${series.id}`)
              }
            />
          )}
          {canNewVendor && (
            <CreateVendorModal
              {...modalCommon}
              open={create?.kind === 'vendor'}
              onCreated={(v) =>
                create?.kind === 'vendor' && create.onCreated
                  ? create.onCreated(v)
                  : navigate(`/vendors/${v.id}`)
              }
            />
          )}
          {canNewHoa && (
            <CreateHoaModal
              open={create?.kind === 'hoa'}
              onClose={closeCreate}
              onCreated={(h) =>
                create?.kind === 'hoa' && create.onCreated
                  ? create.onCreated(h)
                  : navigate(`/hoa/${h.id}`)
              }
            />
          )}
        </>
      )}
    </PaletteContext.Provider>
  );
};
